/**
 * Zod schemas for the explicit follow-graph API payloads (FRE-925).
 *
 * Mirror of the `@loci/types` follow shapes and the OpenAPI contract for the
 * follow routes. The list-query schema coerces `limit`/`offset` from their
 * string query-param form and applies safe bounds so the followed-list
 * endpoints can't be asked for an unbounded page.
 */

import { z } from 'zod';

/** Artist follow/unfollow/get-state payload — wire key is `artistId`. */
export const artistFollowStateSchema = z.object({
  artistId: z.string(),
  following: z.boolean(),
});

/** Venue follow/unfollow/get-state payload — wire key is `venueId`. */
export const venueFollowStateSchema = z.object({
  venueId: z.string(),
  following: z.boolean(),
});

/**
 * Union of the per-entity follow-state payloads. Mirrors the wire: each route
 * returns its entity-specific key (`artistId` / `venueId`), never a shared
 * `entityId`.
 */
export const followStateSchema = z.union([artistFollowStateSchema, venueFollowStateSchema]);

/** Derived "next show" for a followed artist; null when none upcoming. */
export const followedArtistNextShowSchema = z
  .object({
    eventId: z.string(),
    title: z.string(),
    date: z.string(),
    venueName: z.string().nullable(),
  })
  .nullable();

/** One enriched followed-artist list entry. */
export const followedArtistSchema = z.object({
  artistId: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable().optional(),
  genres: z.array(z.string()),
  followedAt: z.string(),
  nextShow: followedArtistNextShowSchema,
});

/** One enriched followed-venue list entry. */
export const followedVenueSchema = z.object({
  venueId: z.string(),
  name: z.string(),
  neighborhood: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  followedAt: z.string(),
  lastSeenAt: z.string(),
  newEventCount: z.number().int().min(0),
});

/** Pagination envelope shared by both followed-list responses. */
export const followedListPaginationSchema = z.object({
  total: z.number().int().min(0),
  limit: z.number().int().min(0),
  offset: z.number().int().min(0),
  hasMore: z.boolean(),
});

/** Response body for `GET /api/v1/users/me/followed-artists`. */
export const followedArtistsResponseSchema = z.object({
  artists: z.array(followedArtistSchema),
  pagination: followedListPaginationSchema,
});

/** Response body for `GET /api/v1/users/me/followed-venues`. */
export const followedVenuesResponseSchema = z.object({
  venues: z.array(followedVenueSchema),
  pagination: followedListPaginationSchema,
});

/** Response body for `POST /api/v1/users/me/followed-venues/seen`. */
export const markSeenResponseSchema = z.object({
  markedCount: z.number().int().min(0),
  seenAt: z.string(),
});

/**
 * Query-param schema for the followed-list endpoints. Coerces the string
 * `limit`/`offset` query params to bounded integers. `limit` defaults to 50,
 * caps at 100; `offset` defaults to 0. Invalid values are rejected (the route
 * returns 400 INVALID_QUERY_PARAMETERS) rather than silently clamped, so the
 * client gets an explicit signal.
 */
export const followedListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export type ArtistFollowStateValidated = z.infer<typeof artistFollowStateSchema>;
export type VenueFollowStateValidated = z.infer<typeof venueFollowStateSchema>;
export type FollowStateValidated = z.infer<typeof followStateSchema>;
export type FollowedArtistValidated = z.infer<typeof followedArtistSchema>;
export type FollowedVenueValidated = z.infer<typeof followedVenueSchema>;
export type FollowedArtistsResponseValidated = z.infer<typeof followedArtistsResponseSchema>;
export type FollowedVenuesResponseValidated = z.infer<typeof followedVenuesResponseSchema>;
export type FollowedListQueryValidated = z.infer<typeof followedListQuerySchema>;
